import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export function usePaymentResult() {
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);

  const bookingId = searchParams.get('booking_id');
  const orderId = searchParams.get('order_id') || searchParams.get('OrderId');
  const successIndicator = searchParams.get('success_indicator') || searchParams.get('successIndicator');

  const verifyPayment = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      // Build query params - preserve all parameters from URL
      const params = new URLSearchParams();
      if (orderId) params.append('order_id', orderId);
      if (successIndicator) params.append('success_indicator', successIndicator);

      // Get all query params from current URL to pass through
      const urlParams = new URLSearchParams(window.location.search);
      const message = urlParams.get('Message') || urlParams.get('message');
      const orderIdFromUrl = urlParams.get('OrderId') || urlParams.get('orderId');

      if (message) params.append('Message', message);
      if (orderIdFromUrl) params.append('OrderId', orderIdFromUrl);

      const response = await fetch(
        `${API_URL}/api/bookings/${bookingId}/payment/verify?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Parse response based on status
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // If JSON parsing fails, create error object
        console.error('JSON parsing error:', jsonError);
        data = {
          error: 'Failed to parse response',
          status: response.status
        };
      }

      if (!response.ok) {
        console.error('Verification failed:', data);
        throw new Error(data.detail || data.error || 'Failed to verify payment');
      }

      setResult(data);

      // Fetch location details
      if (data.location_id) {
        const locationResponse = await fetch(`${API_URL}/api/locations/${data.location_id}`);
        if (locationResponse.ok) {
          const locationData = await locationResponse.json();
          setLocation(locationData);
        }
      }

      if (data.is_success) {
        toast.success('Payment confirmed successfully!');
      } else if (data.payment_status === 'pending') {
        toast.info('Payment is being processed...');
      } else {
        toast.error('Payment was not successful');
      }

    } catch (err) {
      console.error('Payment verification error:', err);
      setError(err.message);
      toast.error(`Failed to verify payment: ${err.message}`);
    } finally {
      setVerifying(false);
    }
  }, [bookingId, orderId, successIndicator]);

  const lookupBookingByOrder = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings/lookup-by-order/${orderId}`);

      if (response.ok) {
        const data = await response.json();
        // Update URL with booking_id and reload
        const params = new URLSearchParams(window.location.search);
        params.set('booking_id', data.booking_id);
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);

        // Now verify with the booking_id
        verifyPayment();
      } else {
        throw new Error('Failed to lookup booking');
      }
    } catch (err) {
      console.error('Booking lookup error:', err);
      setError('Failed to find booking for this payment');
      setVerifying(false);
    }
  }, [orderId, verifyPayment]);

  useEffect(() => {
    if (bookingId) {
      verifyPayment();
    } else if (orderId) {
      // If no booking_id but we have orderId, look it up
      lookupBookingByOrder();
    } else {
      setError('Missing booking information');
      setVerifying(false);
    }
  }, [bookingId, orderId, verifyPayment, lookupBookingByOrder]);

  // New function to check payment status with Pocket Pay gateway
  const checkGatewayStatus = async () => {
    setVerifying(true);
    setError(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(
        `${API_URL}/api/bookings/${bookingId}/payment/check-gateway-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('JSON parse error:', jsonError);
          throw new Error('Invalid server response');
        }
      } else {
        throw new Error('Server returned non-JSON response');
      }

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to check payment status');
      }

      // Update UI based on gateway response
      if (data.status === 'confirmed') {
        // Payment confirmed!
        setResult({
          is_success: true,
          payment_status: 'confirmed',
          message: data.message,
          status_details: { message: data.message }
        });
        toast.success('Payment confirmed!');

        // Reload page to show updated booking details
        setTimeout(() => window.location.reload(), 1500);
      } else if (data.status === 'failed') {
        // Payment failed or timeout
        setResult({
          is_success: false,
          payment_status: 'failed',
          message: data.message,
          status_details: { message: data.message }
        });
        toast.error(data.message);
      } else if (data.status === 'pending') {
        // Still pending
        toast.info('Payment still pending. Please try again in a few minutes.');
      } else if (data.status === 'error') {
        // Error checking status
        setError(data.message || 'Failed to check payment status');
        toast.error(data.message || 'Failed to check payment status');
      }

    } catch (err) {
      console.error('Gateway status check error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setVerifying(false);
    }
  };

  return { bookingId, verifying, result, error, location, checkGatewayStatus };
}
